import { useQuery } from "@tanstack/react-query";
import {
  GET_COMIC_QUERY_KEY,
  getComic,
} from "../../reader/api/readerScreenApi";

/**
 * Resolve a comic slug to its starter release UUID so the fullscreen preview
 * can hand a UUID off to FeedPagesViewer.
 *
 * Compact-card payloads (recommendation rails, genre dive, featured creator,
 * bundle) carry only comic identity — no release UUID. We fetch the comic
 * record once per click; React Query dedupes parallel calls and caches the
 * result so reopening the same comic is instant.
 *
 * Returns `null` while loading or on error so callers can render a spinner
 * before handing off to the page viewer.
 */
export function useComicStarterRelease(slug: string | null) {
  const { data, isLoading, error } = useQuery({
    enabled: !!slug,
    queryKey: [...GET_COMIC_QUERY_KEY, slug, "starter-release"],
    queryFn: async () => {
      if (!slug) return null;
      const response = await getComic(slug);
      return response.data.payload.results;
    },
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000,
  });

  // first_release_uuid is the cheap path; releases[0].key (the public UUID
  // field on Release) is the fallback for response shapes that don't
  // surface the precomputed field. Keep both so a deploy where one is
  // missing still renders the preview.
  const releaseUuid =
    data?.first_release_uuid ?? data?.releases?.[0]?.key ?? null;

  return {
    comic: data ?? null,
    releaseUuid,
    isLoading,
    error,
  };
}
